import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { map, startWith, pairwise } from 'rxjs/operators';
import {QueryList} from '@angular/core';
import {RisikoKfzComponent} from '../app/components/calculatekfz/risikokfz/risikokfz.component';
import { ILookupValue } from '../app/models/lookupvalues';

export const toggleEditMode = (control: AbstractControl, show: boolean, disabled: boolean = false, reset: boolean = true): void => {
  const options = { emitEvent: false };
  if (show === true) {
    if (disabled === false) {
      control.enable(options);
    }
  } else if (control !== null) {
    control.disable(options);
    if (reset === true) {
      control.reset(null, options);
    }
  }
};

export const toggleEnabledDisabled = (control: AbstractControl): void => {
  if (control.disabled) {
    control.enable();
  } else {
    control.disable();
  }
};

export const getExpertMode = (route: ActivatedRoute): boolean => {
  const expertMode: boolean = route.snapshot.data.expertMode;
  return expertMode && expertMode === true ? true : false;
};

export const getMotorrad = (route: ActivatedRoute): boolean => {
  const motorrad: boolean = route.snapshot.data.motorrad;
  return motorrad && motorrad === true ? true : false;
};

export const getOffertBaseId = (route: ActivatedRoute): number => {
  const offertBaseId: string = route.snapshot.paramMap.get('offertBaseId');
  return offertBaseId ? Number(offertBaseId) : null;
};

export const getOffertBase = (route: ActivatedRoute): boolean => {
  const offertBase: boolean = route.snapshot.data.offertBase;
  return offertBase && offertBase === true ? true : false;
};

export const getPartnerId = (route: ActivatedRoute): number => {
  const partnerId: string = route.snapshot.paramMap.get('partnerId');
  return partnerId ? Number(partnerId) : null;
};

export const getPartner = (route: ActivatedRoute): boolean => {
  const partner: boolean = route.snapshot.data.partner;
  return partner && partner === true ? true : false;
};

export const getVerifyOffert = (route: ActivatedRoute): boolean => {
  const verifyOffert: boolean = route.snapshot.data.verifyOffert;
  return verifyOffert && verifyOffert === true ? true : false;
};

export const getOffertAntrag = (route: ActivatedRoute): boolean => {
  const offertAntrag: boolean = route.snapshot.data.offertAntrag;
  return offertAntrag && offertAntrag === true ? true : false;
};

export const getFertigstellen = (route: ActivatedRoute): boolean => {
  const fertigstellen: boolean = route.snapshot.data.fertigstellen;
  return fertigstellen && fertigstellen === true ? true : false;
};

export const getResetPasscode = (route: ActivatedRoute): boolean => {
  const resetPasscode: boolean = route.snapshot.data.resetPasscode;
  return resetPasscode && resetPasscode === true ? true : false;
};

export const getQuery = (): string => {
  const query: string = sessionStorage.getItem('q');
  return query ? query : null;
};


export const flattenArray = (obj: any, map: Map<string, any> = null) => {
  if (map === null) {
    map = new Map<string, any>();
  }
  obj.forEach(function (element) {
    for (const [key, value] of Object.entries(element)) {
      if (Array.isArray(value)) {
        flattenArray(value, map);
      } else {
        let i = 0;
        while (map.has(key + (i > 0 ? '_' + i : ''))) {
          i++;
        }
        map.set(key + (i > 0 ? '_' + i : ''), value);
      }
    }
  });
  return map;
};

export const valueChanges = (control: UntypedFormArray): Subject<any> => {
  const change$: Subject<any> = new Subject();
  control.valueChanges
    .pipe(
      map(value => flattenArray(value)),
      startWith(flattenArray([{}])),
      pairwise(),
    ).subscribe(([prev, next]: [any, any]) => {
      next.forEach((value: any, key: string) => {
        if (JSON.stringify(prev.get(key)) !== JSON.stringify(next.get(key))) {
          // console.log(key + ' changed from ' + (!prev.has(key) ? 'n/a' : prev.get(key)) + ' to ' + next.get(key));
          change$.next({ key: key, prev: (!prev.has(key) ? null : prev.get(key)), next: next.get(key) });
        }
      });
    });
  return change$;
};

export const getCurrentScrollPosition = (): number => {
  return window.scrollY || window.pageYOffset || document.body.scrollTop + (document.documentElement && document.documentElement.scrollTop || 0);
};

export const convertBase64ToPdf = (base64: string): Blob => {
  const binary = atob(base64);
  const data = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    data[i] = binary.charCodeAt(i);
  }
  return new Blob([data], { type: 'application/pdf' });
};

export const addRemoveFormArray = (groups: UntypedFormArray, anzahl: number, createFormGroup: () => UntypedFormGroup): void => {
  if (anzahl > groups.length) {
    // Die Differenz hinzufügen
    const anzahlAdd = anzahl - groups.length;
    for (let i = 0; i < anzahlAdd; i++) {
      groups.push(createFormGroup());
    }
  }
  if (anzahl < groups.length) {
    // Die Differenz löschen
    const anzahlRemove = groups.length - anzahl;
    for (let i = 0; i < anzahlRemove; i++) {
      groups.removeAt(groups.length - 1);
    }
  }
};

export const showRegion = (motorrad: boolean, risikoKfzComponents: QueryList<RisikoKfzComponent>): boolean => {
  return !motorrad && risikoKfzComponents?.first.fahrzeugart.value &&
  (risikoKfzComponents?.first.fahrzeugart.value.Key === '15' ||
    risikoKfzComponents?.first.fahrzeugart.value.Key === '22' ||
    risikoKfzComponents?.first.fahrzeugart.value.Key === '6') ? true : false;
};

export const showBmStufe = (motorrad: boolean, risikoKfzComponents: QueryList<RisikoKfzComponent>): boolean => {
  return !motorrad && (risikoKfzComponents?.first.fahrzeugart.value && (risikoKfzComponents?.first.fahrzeugart.value.Key === '15' ||
    risikoKfzComponents?.first.fahrzeugart.value.Key === '6' || risikoKfzComponents?.first.fahrzeugart.value.Key === '22')) ? true : false;
};

export const preselectResetLookupValue = (control: AbstractControl, data: ILookupValue[], preselect: () => void, notpreselect: () => void, reset: () => void): void => {
  const options = { emitEvent: false };
  if (control.value) {
    if (!data.some(x => x.Id === control.value.Id && x.Key === control.value.Key && x.Value === control.value.Value)) {
      control.setValue(null, options);
      reset();
    }
  } else {
    const values = data.filter(x => x.Id != null);
    if (values.length === 1) {
      control.setValue(values[0], options);
      preselect();
    } else {
      notpreselect();
    }
  }
};

export const removeDiacritics = (str: string): string => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

export const removeSpecialChars = (str: string): string => {
  let content = (str ?? '').replaceAll(' ', '');
  content = content.replace(/[^A-Za-z 0-9]/g,'');
  return content;
};

export const formatIban = (str: string): string => {
  return str
    .replace(/[^\dA-Z]/gi, '') // accept only digits & letters
    .toUpperCase() // convert lowercase characters to uppercase
    .replace(/(.{4})/g, '$1 ') // insert a space after every 4 characters
    .trim();  // trim spaces at the end of the string if any (when IBAN length is even)
};

export const countSpaces = (text: string): number => {
  const spaces = text.match(/(\s+)/g);
  return spaces ? spaces.length : 0;
};

export const getInvalidControlKeys = (group: UntypedFormGroup | UntypedFormArray, invalidControlKeys: string[]): void => {
  Object.keys(group.controls).forEach((key: string) => {
    const abstractControl = group.controls[key];
    if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
      getInvalidControlKeys(abstractControl, invalidControlKeys);
    } else {
      if (abstractControl.invalid) {
        invalidControlKeys.push(key);
      }
    }
  });
};

export const validateExcludeAgbs = (group: UntypedFormGroup | UntypedFormArray): boolean => {
  const invalidControlKeys = [];
  getInvalidControlKeys(group, invalidControlKeys);
  return invalidControlKeys.length === 0 || invalidControlKeys.length === 1 && invalidControlKeys.includes('allgemeineVertragsgrundlagen') ? true : false;
};

export const timestampToDateKey = (ts: number): number => {
  const date = new Date(ts * 1000);
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
};
